/**
 * ModelSettingsPanel — admin control to pick which Claude model each AI
 * generator uses (cost vs depth), defaulting to the recommended mapping.
 * Changes apply to the NEXT generation of each artifact. Global per-generator.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Cpu, ChevronDown, ChevronUp, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function ModelSettingsPanel() {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/assessments/llm-models`);
      setCfg(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load model settings");
    }
  }, []);

  useEffect(() => { if (open && !cfg) load(); }, [open, cfg, load]);

  const setGen = (g, v) => setCfg((c) => ({ ...c, settings: { ...c.settings, [g]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/assessments/llm-models`, { settings: cfg.settings });
      setCfg((c) => ({ ...c, settings: data.settings }));
      toast.success("AI model settings saved — applies to the next generation");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="model-settings-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2" data-testid="model-settings-toggle">
        <span className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]">
          <Cpu size={14}/> AI model settings
        </span>
        {open ? <ChevronUp size={16} className="text-zinc-400"/> : <ChevronDown size={16} className="text-zinc-400"/>}
      </button>

      {open && cfg && (
        <div className="mt-4 space-y-3" data-testid="model-settings-form">
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Pick the model per generator. Opus = deepest (highest cost); Sonnet = strong + ~5–10× cheaper; Haiku = fastest/cheapest. Applies to the next generation.
          </p>
          {cfg.generators.map((g) => (
            <div key={g.key} className="flex flex-col gap-1">
              <label className="text-[13px] text-zinc-800">
                {g.label}
                {cfg.cost_estimates?.[g.key]?.[cfg.settings[g.key]] != null && (
                  <span className="ml-1.5 text-[11px] text-zinc-400">~${cfg.cost_estimates[g.key][cfg.settings[g.key]]}/run</span>
                )}
              </label>
              <select
                value={cfg.settings[g.key]}
                onChange={(e) => setGen(g.key, e.target.value)}
                className="w-full max-w-full box-border border border-zinc-300 text-[13px] px-2 py-1.5 bg-white truncate"
                data-testid={`model-select-${g.key}`}
              >
                {cfg.models.map((m) => (
                  <option key={m.key} value={m.key}>{m.label}{cfg.defaults[g.key] === m.key ? " (default)" : ""}</option>
                ))}
              </select>
            </div>
          ))}
          {cfg.cost_note && <p className="text-[10px] text-zinc-400">{cfg.cost_note}</p>}
          <button type="button" onClick={save} disabled={busy} className="ek-btn ek-btn-primary w-full text-[13px] disabled:opacity-50" data-testid="model-settings-save">
            <Save size={13}/> {busy ? "Saving…" : "Save model settings"}
          </button>
          <AiBudgetControl />
        </div>
      )}
    </div>
  );
}

function AiBudgetControl() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/assessments/ai-budget`).then(({ data }) => setB(data)).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/assessments/ai-budget`,
        { enabled: b.enabled, cap_usd: Number(b.cap_usd) || 0 });
      setB(data);
      toast.success(data.enabled
        ? `AI budget cap on — new generations blocked past ~$${data.cap_usd.toFixed(2)}/run`
        : "AI budget cap off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save budget");
    } finally { setBusy(false); }
  };
  if (!b) return null;
  return (
    <div className="mt-4 pt-4 border-t border-zinc-200" data-testid="ai-budget-control">
      <label className="flex items-center gap-2 text-[13px] text-zinc-800 font-medium">
        <input type="checkbox" checked={b.enabled} data-testid="ai-budget-enabled"
               onChange={(e) => setB((s) => ({ ...s, enabled: e.target.checked }))} />
        Cap AI spend per run (blocks new generations if charges get out of hand)
      </label>
      {b.enabled && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[12px] text-zinc-500">Cap per run (USD, indicative)</span>
          <span className="text-zinc-500 text-[13px]">$</span>
          <input type="number" min="0" step="0.5" value={b.cap_usd} data-testid="ai-budget-cap"
                 onChange={(e) => setB((s) => ({ ...s, cap_usd: e.target.value }))}
                 className="w-24 border border-zinc-300 text-[13px] px-2 py-1.5 tabular-nums" />
        </div>
      )}
      <p className="text-[10px] text-zinc-400 mt-2 leading-relaxed">
        A soft guardrail on the indicative estimate. Cached views and already-generated artefacts stay
        available; only NEW generations past the cap are blocked until you raise or turn it off.
      </p>
      <button type="button" onClick={save} disabled={busy}
              className="ek-btn ek-btn-outline w-full text-[13px] mt-2 disabled:opacity-50" data-testid="ai-budget-save">
        <Save size={13}/> {busy ? "Saving…" : "Save budget cap"}
      </button>
    </div>
  );
}
